import { supabase } from '@/core/database/supabase';
import type { 
  ConstructionContract, 
  ContractStats, 
  ContractFilters 
} from '../types/construction-contracts.types';

export const ConstructionContractsService = {
  /**
   * Danh sách hợp đồng thi công với phân trang
   */
  async getContracts(companyId: string, filters: ContractFilters = {}): Promise<{ data: ConstructionContract[], total: number }> {
    const page = filters.page || 1;
    const pageSize = filters.pageSize || 10;
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('construction_contracts')
      .select(`
        *,
        project:projects(id, name, code),
        pm:user_profiles!pm_user_id(id, full_name, avatar_url:avatar_path),
        site_manager:user_profiles!site_manager_user_id(id, full_name, avatar_url:avatar_path),
        qs:user_profiles!qs_user_id(id, full_name, avatar_url:avatar_path)
      `, { count: 'exact' })
      .eq('company_id', companyId);

    if (filters.status && filters.status !== 'all') query = query.eq('status_code', filters.status);
    if (filters.type && filters.type !== 'all') query = query.eq('contract_type', filters.type);
    if (filters.projectId) query = query.eq('project_id', filters.projectId);
    
    if (filters.search) {
      query = query.or(`contract_name.ilike.%${filters.search}%,contract_code.ilike.%${filters.search}%,contractor_name.ilike.%${filters.search}%`);
    }

    query = query
      .order('created_at', { ascending: false })
      .range(from, to);

    const { data, error, count } = await query;

    if (error) throw error;
    return {
      data: (data || []) as ConstructionContract[],
      total: count || 0
    };
  },

  /**
   * Chi tiết hợp đồng
   */
  async getContractDetail(id: string): Promise<ConstructionContract | null> {
    const { data, error } = await supabase
      .from('construction_contracts')
      .select(`
        *,
        project:projects(id, name, code),
        pm:user_profiles!pm_user_id(id, full_name, avatar_url:avatar_path),
        site_manager:user_profiles!site_manager_user_id(id, full_name, avatar_url:avatar_path),
        qs:user_profiles!qs_user_id(id, full_name, avatar_url:avatar_path)
      `)
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;
    return data as ConstructionContract;
  },

  /**
   * Thống kê hợp đồng
   */
  async getContractStats(companyId: string): Promise<ContractStats> {
    const { data, error } = await supabase
      .from('construction_contracts')
      .select('contract_value, status_code, retention_rate')
      .eq('company_id', companyId);

    if (error) throw error;

    const stats: ContractStats = {
      totalValue: data.reduce((sum, c) => sum + (c.contract_value || 0), 0),
      activeContracts: data.filter(c => c.status_code === 'active').length,
      pendingValue: data.filter(c => ['draft', 'pending_review', 'pending_approval'].includes(c.status_code))
                         .reduce((sum, c) => sum + (c.contract_value || 0), 0),
      avgRetentionRate: data.length > 0 
        ? data.reduce((sum, c) => sum + (c.retention_rate || 0), 0) / data.length 
        : 0,
    };

    return stats;
  },

  /**
   * CRUD
   */
  async createContract(data: Partial<ConstructionContract>): Promise<ConstructionContract> {
    const { data: result, error } = await supabase
      .from('construction_contracts')
      .insert([data])
      .select()
      .single();

    if (error) throw error;
    return result as ConstructionContract;
  },

  async updateContract(id: string, updates: Partial<ConstructionContract>): Promise<ConstructionContract> {
    const { data, error } = await supabase
      .from('construction_contracts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as ConstructionContract;
  },

  async deleteContract(id: string): Promise<void> {
    const { error } = await supabase
      .from('construction_contracts')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }
};
